import { Injectable } from '@angular/core';
import { SelectItem } from '@psei/ng/api';
import { FormChangeService } from 'projects/ppb-shared-library/src/lib/services/form-change.service';
import { LocalStorageService } from 'projects/ppb-shared-library/src/lib/services/local-storage.service';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { UserService } from './user.service';

export function languageProviderFactory(provider: LanguageService): () => Promise<boolean> {
  return () => provider.load();
}

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  load(): Promise<boolean> {
    return new Promise((resolve) => {
      this.setPreferredLanguage();
      resolve(true);
    })
  }

  static readonly langItems = Object.freeze([
    SelectItem.create('pl', 'Polski (pl)'),
    SelectItem.create('en', 'English (en)'),
  ]);

  static readonly availableLanguages = Object.freeze(['pl', 'en']);

  selectedLanguage$: BehaviorSubject<string> = new BehaviorSubject<string>('pl');
  private readonly defaultLanguage: string = 'pl';

  constructor(private localStorageService: LocalStorageService,
              private formChangeService: FormChangeService,
              private userService: UserService
  ) {
  }

  handleLanguageChange(lang: string, isLogin: boolean = false): void {
    if (isLogin) {
      this.setLanguage(lang);
      this.redirectToLang(lang);
      return;
    }

    this.formChangeService.isPreventPopup$.next(true);
    this.userService.changeAccountLanguage(lang.toString()).subscribe(() => {
      this.setLanguage(lang);
      this.redirectToLang(lang);
    });
  }

  redirectToLang(lang: string): void {
    if (!environment.production) {
      return;
    }

    const redirectUrl: string = LanguageService.getRedirectUrl(lang, window.location);
    if (redirectUrl) {
      window.location.href = redirectUrl;
    }
  }

  static getRedirectUrl(lang: string, location: Location): string | undefined {
    const uriAppLanguage = location.pathname.split('/')[1];
    const hasLanguage = LanguageService.availableLanguages.indexOf(uriAppLanguage) !== -1;
    let redirectUrl: string;

    if (hasLanguage && uriAppLanguage !== lang) {
      const path = location.pathname.replace(`/${uriAppLanguage}/`, `/${lang}/`);
      redirectUrl = `${location.origin}${path}${location.search}`;
    }

    if (!hasLanguage) {
      const path = `/${lang}${location.pathname}`;
      redirectUrl = `${location.origin}${path}${location.search}`;
    }
    return redirectUrl;
  }

  setLanguage(lang: string): void {
    this.selectedLanguage$.next(
      LanguageService.langItems.find(
        (i) => i.value === lang
      ).value)
    this.localStorageService.setLanguageItem(lang);
  }

  setPreferredLanguage(): void {
    const langs: string[] = LanguageService.langItems.map(
      ({ value }) => value
    );
    const preferredLang = this.localStorageService.getPreferredLanguage();
    const chosenLang = langs.includes(preferredLang) ? preferredLang : this.defaultLanguage;
    this.setLanguage(chosenLang);
    this.redirectToLang(chosenLang);
  }
}
